﻿import React, { useState, useEffect, useCallback } from 'react';
import { User, ChevronDown, ChevronRight, Activity, Users, Plus, Trash2, Edit3, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { PersonalityEvolution } from './PersonalityEvolution';
import { ContactsPanel } from './ContactsPanel';
import { apiFetch } from '../services/apiClient';

interface PersonalityConfig {
  id: string;
  name: string;
  version: string;
  coreMotivation: string;
  coreMotivationZh?: string;
  coreMotivationSummary?: {
    role: string;
    channels: string;
    principle: string;
    style: string;
  };
  behavioralBoundaries: string[];
  behavioralBoundariesZh?: string[];
  expressionStyle: {
    persona: string;
    personaZh?: string;
    tone: string;
    verbosity: string;
    languages: string[];
    vocabularyHints?: string[];
  };
  toolPolicy: {
    allowedTools: string[];
    requireConfirmation: string[];
    forbiddenTools: string[];
    maxIterations: number;
    securityOverrides?: Record<string, string>;
  };
  memoryPolicy: {
    retrieveLimit: number;
    minConfidence: number;
    includeTypes: string[];
    autoExtract: boolean;
  };
  ttsVoiceId?: string;
  personalityVector?: {
    cognitiveStyle: { analytical: number; intuitive: number; systematic: number; creative: number };
    socialStyle: { warmth: number; directness: number; playfulness: number; formality: number };
  };
  evolutionConfig?: {
    plasticity: number;
    minMemoriesForEvolution: number;
    minConnectionForEvolution: number;
    cooldownMs: number;
    maxMutationsPerStep: number;
  };
  lastEvolvedAt?: string | null;
  growthState?: {
    version: number;
    lastUpdatedAt: string;
    ownerInterests: string[];
    ownerExpressions: string[];
    communicationPatterns: string[];
    adaptationNotes: string[];
    ownerProfile?: {
      memoryCount: number;
      dominantTone: string;
      formalityLevel: number;
      emotionalExpressiveness: number;
    };
  };
  evolutionFrozenAt?: string | null;
}

export function PersonalityEditor({ t }: { t?: any }) {
  const [tab, setTab] = useState<'personality' | 'contacts'>('personality');
  const [config, setConfig] = useState<PersonalityConfig | null>(null);
  const [allList, setAllList] = useState<PersonalityConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<PersonalityConfig | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    identity: true,
    growth: true,
    boundaries: false,
    expression: false,
    evolution: true,
    tools: false,
    memory: false,
  });
  const [showOriginalPrompt, setShowOriginalPrompt] = useState(false);
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);

  // v3.12.62: 字段值汉化映射（人格 vector keys / tone / verbosity / 工具名 等）
  const COG_STYLE_KEY_ZH: Record<string, string> = {
    analytical: '分析型', intuitive: '直觉型', systematic: '系统型', creative: '创新型',
  };
  const SOC_STYLE_KEY_ZH: Record<string, string> = {
    warmth: '温暖', directness: '直接', playfulness: '活泼', formality: '正式',
  };
  const TONE_ZH: Record<string, string> = {
    neutral: '中性', warm: '温暖', professional: '专业', technical: '技术', playful: '活泼', inspiring: '鼓舞',
  };
  const VERBOSITY_ZH: Record<string, string> = {
    concise: '简洁', balanced: '均衡', detailed: '详细',
  };
  const VOICE_ZH: Record<string, string> = {
    longxiaochun_v3: '龙小春', longhan_v3: '龙涵', longfei_v3: '龙飞', longanhuan_v3: '龙安欢',
    longcheng_v3: '龙诚', longhua_v3: '龙华', longjing_v3: '龙晶', longmiao_v3: '龙淼',
    longshu_v3: '龙舒', longwan_v3: '龙婉', longxiang_v3: '龙翔', longying_v3: '龙英',
  };
  const trKey = (zhMap: Record<string, string>, k: string) => (isZh ? (zhMap[k] || k) : k);
  const trTone = (k: string) => (isZh ? (TONE_ZH[k] || k) : k);
  const trVerbosity = (k: string) => (isZh ? (VERBOSITY_ZH[k] || k) : k);
  const trVoice = (k?: string) => (k && isZh ? (VOICE_ZH[k] || k) : (k || ''));

  const toggleSection = (s: string) => setExpandedSections(prev => ({ ...prev, [s]: prev[s] === true ? false : true }));

  const fetchAll = useCallback(() => {
    apiFetch('/api/personalities')
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setAllList(data);
          const active = data.find((p: PersonalityConfig) => p.id === config?.id) || data[0];
          setConfig(active);
        }
      })
      .catch((err: unknown) => { setError(err instanceof Error ? err.message : '网络错误'); })
      .finally(() => setLoading(false));
  }, [config?.id]);

  useEffect(() => { fetchAll(); }, []);

  // v3.10.0: switch personality
  const switchTo = (p: PersonalityConfig) => {
    setConfig(p);
    setShowDropdown(false);
    // v3.12.35: 简单 fire-and-forget，不引入 AbortController（老旧 WebView 不支持会崩）
    apiFetch('/api/personalities/' + p.id + '/activate', { method: 'POST' }).catch(() => {});
  };

  // v3.10.0: create new personality
  const handleCreate = async () => {
    const name = prompt(ui('新人格名称（中文或英文）：', 'New personality name:'));
    if (!name) return;
    setCreating(true);
    try {
      const r = await apiFetch('/api/personalities', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error); }
      fetchAll();
      toast.success(ui('已创建新人格', 'Personality created'));
    } catch (err: unknown) { toast.error(err instanceof Error ? err.message : String(err)); }
    finally { setCreating(false); }
  };

  // v3.10.0: delete personality
  const handleDelete = async () => {
    if (!config || allList.length <= 1) return;
    if (!window.confirm('确定要删除「' + config.name + '」吗？此操作不可撤销。')) return;
    try {
      const r = await apiFetch('/api/personalities/' + config.id, { method: 'DELETE' });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error); }
      fetchAll();
      toast.success(ui('已删除', 'Deleted'));
    } catch (err: unknown) { toast.error(err instanceof Error ? err.message : String(err)); }
  };

  // v3.10.1: enter edit mode (clone current config)
  const enterEdit = () => { setDraft(JSON.parse(JSON.stringify(config))); setEditMode(true); };
  const cancelEdit = () => { setEditMode(false); setDraft(null); };

  // v3.10.1: save edited config
  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const r = await apiFetch('/api/personalities/' + draft.id, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(draft) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error); }
      const updated = await r.json();
      setConfig(updated);
      setEditMode(false);
      setDraft(null);
      toast.success(ui('已保存', 'Saved'));
    } catch (err: unknown) { toast.error(err instanceof Error ? err.message : String(err)); }
    finally { setSaving(false); }
  };

  // Helper: update a field in draft
  const updateDraft = (path: string[], value: any) => {
    if (!draft) return;
    const clone = JSON.parse(JSON.stringify(draft));
    let obj = clone;
    for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
    obj[path[path.length - 1]] = value;
    setDraft(clone);
  };

  const activeConfig = editMode && draft ? draft : config;

  if (loading) {
    return (
      <div className="animate-in fade-in space-y-8 duration-500">
        <div className="xf-panel flex items-center gap-3 p-5">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-indigo-500/20 bg-indigo-500/10 text-indigo-300">
            <User size={20} />
          </span>
          <h3 className="text-xl font-black uppercase tracking-[0.08em] text-white/90">{t?.lumiCore || ui('小枫 人格核心配置', '小枫 Core Config')}</h3>
        </div>
        <p className="xf-panel p-5 text-sm text-white/40">{t?.loadingPersonalities || ui('加载中...', 'Loading...')}</p>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="animate-in fade-in space-y-8 duration-500">
        <div className="xf-panel flex items-center gap-3 p-5">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-indigo-500/20 bg-indigo-500/10 text-indigo-300">
            <User size={20} />
          </span>
          <h3 className="text-xl font-black uppercase tracking-[0.08em] text-white/90">{t?.lumiCore || ui('小枫 人格核心配置', '小枫 Core Config')}</h3>
        </div>
        <div className="xf-panel p-6 text-center space-y-4">
          <p className="text-sm text-white/40">{t?.noPersonalitiesDefined || ui('未找到配置。', 'No configuration found.')}</p>
          {error && <p className="text-xs text-red-400/70 font-mono">{error}</p>}
          <button onClick={() => { setLoading(true); setError(null); window.location.reload(); }} className="xf-button-primary h-9 px-4 text-xs border-fuchsia-500/30 bg-fuchsia-500/20 text-fuchsia-300">{ui('重试', 'Retry')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 space-y-6 duration-500">
      {/* v3.10.0: Personality selector + create/delete */}
      {allList.length > 1 && (
        <div className="xf-panel flex items-center gap-2 p-1 relative">
          <button onClick={() => setShowDropdown(!showDropdown)} className="flex flex-1 items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold text-white/80 hover:bg-white/[0.06] transition-colors">
            <User size={14} className="text-indigo-300" />
            <span>{config?.name || '---'}</span>
            <ChevronDown size={14} className="ml-auto text-white/35" />
          </button>
          {showDropdown && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-[#0b1020] border border-white/10 rounded-xl shadow-xl p-1">
              {allList.map(p => (
                <button key={p.id} onClick={() => switchTo(p)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${p.id === config?.id ? 'bg-indigo-500/10 text-indigo-300' : 'text-white/50 hover:bg-white/[0.06]'}`}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.id === config?.id ? '#818cf8' : 'rgba(255,255,255,0.2)' }} />
                  {p.name}
                  <span className="ml-auto text-xs text-white/30 font-mono">v{p.version}</span>
                </button>
              ))}
            </div>
          )}
          <button onClick={handleCreate} disabled={creating} title={ui('新建人格', 'New')} className="p-2 rounded-lg text-white/35 hover:text-white/60 hover:bg-white/[0.06] transition-colors">
            {creating ? <span className="w-4 h-4 border-2 border-indigo-400/30 border-t-indigo-400 rounded-full animate-spin inline-block" /> : <Plus size={16} />}
          </button>
          <button onClick={handleDelete} disabled={allList.length <= 1} title={ui('删除当前人格', 'Delete')} className="p-2 rounded-lg text-white/35 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-20">
            <Trash2 size={16} />
          </button>
        </div>
      )}

      {/* Tab bar */}
      <div className="xf-panel flex items-center gap-1 p-1">
        {[
          { id: 'personality' as const, label: t?.lumiCore || ui('人格核心', 'Personality'), icon: <User size={14} /> },
          { id: 'contacts' as const, label: t?.contacts || ui('联系人', 'Contacts'), icon: <Users size={14} /> },
        ].map(item => (
          <button
            key={item.id}
            onClick={() => setTab(item.id)}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${
              tab === item.id ? 'border-indigo-500/25 bg-indigo-500/10 text-indigo-300' : 'border-transparent text-white/40 hover:bg-white/[0.05] hover:text-white/70'
            }`}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'contacts' ? (
        <ContactsPanel />
      ) : (
        <>
      <div className="xf-panel flex items-center gap-3 p-5">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-indigo-500/20 bg-indigo-500/10 text-indigo-300">
          <User size={20} />
        </span>
        <h3 className="text-xl font-black uppercase tracking-[0.08em] text-white/90">{t?.lumiCore || ui('小枫 人格核心配置', '小枫 Core Config')}</h3>
        <span className="text-xs font-mono text-white/45 bg-white/5 px-2 py-0.5 rounded-full">v{activeConfig.version}</span>
        <div className="flex-1" />
        {!editMode ? (
          <button onClick={enterEdit} className="xf-button h-9 px-3 text-xs border-indigo-500/20 bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20 transition-colors">
            <Edit3 size={14} className="mr-1.5 inline" />{ui('编辑', 'Edit')}
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <button onClick={handleSave} disabled={saving} className="xf-button-primary h-9 px-3 text-xs border-emerald-500/30 bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 transition-colors">
              {saving ? <span className="w-3 h-3 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin inline-block mr-1" /> : <Save size={14} className="mr-1 inline" />}{ui('保存', 'Save')}
            </button>
            <button onClick={cancelEdit} className="xf-button h-9 px-3 text-xs text-white/40 hover:text-white/60 transition-colors">
              <X size={14} className="mr-1 inline" />{ui('取消', 'Cancel')}
            </button>
          </div>
        )}
      </div>

      <p className="text-sm text-white/40 max-w-xl">
        {t?.lumiCoreDesc || ui('小枫 的人格核心会从互动中通过 Hebbian 学习自然成长。这个视图展示当前配置；变化会自动发生，不需要手动硬改。', '小枫\'s core personality evolves organically through Hebbian learning from interactions. This view shows the current configuration — changes happen automatically, not through manual editing.')}
      </p>

      <div className="space-y-4">
        {/* Identity */}
        <Section title={t?.identitySection || ui('身份', 'Identity')} section="identity" expanded={expandedSections} onToggle={toggleSection}>
          {/* Identity card — v3.9.0 redesign */}
          <div className="flex gap-5 p-4 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(192,132,252,0.08) 0%, rgba(99,102,241,0.04) 100%)', border: '1px solid rgba(192,132,252,0.15)' }}>
            <div className="flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #a78bfa, #c084fc)', boxShadow: '0 0 24px rgba(167,139,250,0.3)' }}>
              <User size={28} className="text-white/90" />
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-2xl font-bold text-white/95">{config.name}</span>
                <span className="text-xs font-mono text-violet-300/70 bg-violet-500/10 px-2.5 py-0.5 rounded-full">v{config.version}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${config.evolutionFrozenAt ? 'bg-slate-500/10 text-slate-300 border border-slate-500/20' : 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20'}`}>{config.evolutionFrozenAt ? ui('已冻结', 'Frozen') : ui('活跃', 'Active')}</span>
              </div>
              {editMode ? (
              <textarea value={draft?.coreMotivation || ''} onChange={e => updateDraft(['coreMotivation'], e.target.value)}
                rows={3} className="xf-panel w-full rounded-xl px-3 py-2 text-sm text-white/80 bg-white/[0.04] border border-indigo-500/20 focus:border-indigo-400/40 outline-none resize-y" />
              ) : (
              <div className="space-y-3">
                {/* Chinese summary cards (default) or English original (toggle) */}
                {!showOriginalPrompt && activeConfig.coreMotivationSummary ? (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                      <div className="text-[10px] text-white/40 font-bold uppercase mb-1">{ui('角色定位', 'Role')}</div>
                      <div className="text-sm text-white/75 leading-relaxed">{activeConfig.coreMotivationSummary.role}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                      <div className="text-[10px] text-white/40 font-bold uppercase mb-1">{ui('覆盖渠道', 'Channels')}</div>
                      <div className="text-sm text-white/75 leading-relaxed">{activeConfig.coreMotivationSummary.channels}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                      <div className="text-[10px] text-white/40 font-bold uppercase mb-1">{ui('运行原则', 'Principle')}</div>
                      <div className="text-sm text-white/75 leading-relaxed">{activeConfig.coreMotivationSummary.principle}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                      <div className="text-[10px] text-white/40 font-bold uppercase mb-1">{ui('回复风格', 'Style')}</div>
                      <div className="text-sm text-white/75 leading-relaxed">{activeConfig.coreMotivationSummary.style}</div>
                    </div>
                  </div>
                ) : activeConfig.coreMotivationZh ? (
                  <p className="text-sm text-white/60 leading-relaxed">{activeConfig.coreMotivationZh}</p>
                ) : (
                  <p className="text-sm text-white/60 leading-relaxed">{activeConfig.coreMotivation}</p>
                )}
                {/* Toggle to original English prompt */}
                <button
                  onClick={() => setShowOriginalPrompt(!showOriginalPrompt)}
                  className="flex items-center gap-1.5 text-[11px] text-violet-400/60 hover:text-violet-300 transition-colors"
                >
                  {showOriginalPrompt
                    ? ui('查看中文摘要', 'View Chinese Summary')
                    : ui('查看 System Prompt 原文', 'View System Prompt (English)')}
                </button>
                {showOriginalPrompt && (
                  <div className="mt-2 p-3 rounded-xl bg-white/[0.02] border border-violet-500/15">
                    <div className="text-[10px] text-violet-400/50 font-bold uppercase mb-1">
                      {ui('⚠ 这是 LLM 看到的原始指令，编辑需在 data/personalities.json 中修改', '⚠ This is the raw instruction LLM sees. Edit in data/personalities.json')}
                    </div>
                    <p className="text-xs text-white/45 leading-relaxed font-mono whitespace-pre-wrap">{activeConfig.coreMotivation}</p>
                  </div>
                )}
              </div>
              )}
              <div className="flex items-center gap-4 text-xs text-white/40 font-mono">
                <span>ID: {config.id}</span>
                {config.lastEvolvedAt && <span>{ui('最近演化', 'Last Evolved')}: {new Date(config.lastEvolvedAt).toLocaleDateString()}</span>}
              </div>
            </div>
          </div>
          {config.evolutionFrozenAt && <ReadonlyField label={ui('冻结时间', 'Frozen At')} value={new Date(config.evolutionFrozenAt).toLocaleString()} />}
        </Section>

        {/* Growth State */}
        <Section title={ui('本地成长状态', 'Local Growth State')} section="growth" expanded={expandedSections} onToggle={toggleSection}>
          {config.growthState ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <ReadonlyField label={ui('成长版本', 'Growth Version')} value={String(config.growthState.version)} />
                <ReadonlyField label={ui('最近更新', 'Last Updated')} value={new Date(config.growthState.lastUpdatedAt).toLocaleString()} />
              </div>
              {config.growthState.ownerProfile && (
                <div className="grid grid-cols-2 gap-4">
                  <ReadonlyField label={ui('观察到的语气', 'Observed Tone')} value={config.growthState.ownerProfile.dominantTone} />
                  <ReadonlyField label={ui('画像记忆数', 'Profile Memories')} value={String(config.growthState.ownerProfile.memoryCount)} />
                </div>
              )}
              <ReadonlyField label={ui('用户兴趣', 'Owner Interests')} value={(config.growthState.ownerInterests || []).join(', ') || ui('无', 'none')} />
              <ReadonlyField label={ui('用户表达习惯', 'Owner Expressions')} value={(config.growthState.ownerExpressions || []).join(', ') || ui('无', 'none')} />
              <ReadonlyField label={ui('沟通模式', 'Communication Patterns')} value={(config.growthState.communicationPatterns || []).join('; ') || ui('无', 'none')} />
            </div>
          ) : (
            <p className="text-white/45 text-xs">{ui('还没有本地成长状态。小枫 会从已确认的互动模式中逐步建立。', 'No local growth state yet. 小枫 will build this from confirmed interaction patterns.')}</p>
          )}
        </Section>

        {/* Evolution Vector */}
        <Section title={t?.evolutionVector || ui('演化向量', 'Evolution Vector')} section="evolution" expanded={expandedSections} onToggle={toggleSection}>
          {config.personalityVector ? (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-black uppercase text-white/55 block mb-2">{ui('认知风格', 'Cognitive Style')}</label>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(config.personalityVector.cognitiveStyle).map(([k, v]) => (
                    <div key={k} className="text-center p-3 bg-white/5 rounded-xl">
                      <div className="text-lg font-black text-indigo-400">{(v * 100).toFixed(0)}%</div>
                      <div className="text-[12px] text-white/55">{trKey(COG_STYLE_KEY_ZH, k)}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-black uppercase text-white/55 block mb-2">{ui('社交风格', 'Social Style')}</label>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(config.personalityVector.socialStyle).map(([k, v]) => (
                    <div key={k} className="text-center p-3 bg-white/5 rounded-xl">
                      <div className="text-lg font-black text-violet-400">{(v * 100).toFixed(0)}%</div>
                      <div className="text-[12px] text-white/55">{trKey(SOC_STYLE_KEY_ZH, k)}</div>
                    </div>
                  ))}
                </div>
              </div>
              {config.evolutionConfig && (
                <div className="text-xs text-white/45 space-y-1">
                  <div>{ui('可塑性', 'Plasticity')}: {config.evolutionConfig.plasticity} | {ui('冷却', 'Cooldown')}: {Math.round(config.evolutionConfig.cooldownMs / 86400000)}d | {ui('每步最大变异', 'Max mutations/step')}: {config.evolutionConfig.maxMutationsPerStep}</div>
                  {config.lastEvolvedAt && <div>{ui('最近演化', 'Last evolved')}: {new Date(config.lastEvolvedAt).toLocaleDateString()}</div>}
                </div>
              )}
            </div>
          ) : (
            <p className="text-white/55 text-xs">{t?.evolutionNotInit || ui('演化向量尚未初始化，会在首次互动时生成种子。', 'Evolution vector not yet initialized. It will be seeded on first interaction.')}</p>
          )}
        </Section>

        {/* Expression */}
        <Section title={t?.expressionStyleSection || ui('表达风格', 'Expression Style')} section="expression" expanded={expandedSections} onToggle={toggleSection}>
          <ReadonlyField editMode={editMode} label={t?.personaField || ui('人格表现', 'Persona')}
            value={(isZh && activeConfig.expressionStyle.personaZh) ? activeConfig.expressionStyle.personaZh : activeConfig.expressionStyle.persona}
            onChange={v => updateDraft(['expressionStyle','persona'], v)} />
          <div className="grid grid-cols-2 gap-4">
            <ReadonlyField editMode={editMode} type="select" options={isZh ? ['中性','温暖','专业','技术','活泼','鼓舞'] : ['neutral','warm','professional','technical','playful','inspiring']}
              label={t?.toneField || ui('语气', 'Tone')} value={isZh ? (TONE_ZH[activeConfig.expressionStyle.tone] || activeConfig.expressionStyle.tone) : activeConfig.expressionStyle.tone}
              onChange={v => {
                // 把中文标签反向映射回英文 key 再写入 draft
                const reverseTone: Record<string, string> = isZh
                  ? { '中性':'neutral','温暖':'warm','专业':'professional','技术':'technical','活泼':'playful','鼓舞':'inspiring' }
                  : { neutral:'neutral', warm:'warm', professional:'professional', technical:'technical', playful:'playful', inspiring:'inspiring' };
                updateDraft(['expressionStyle','tone'], reverseTone[v] || v);
              }} />
            <ReadonlyField editMode={editMode} type="select" options={isZh ? ['简洁','均衡','详细'] : ['concise','balanced','detailed']}
              label={t?.verbosityField || ui('详略程度', 'Verbosity')} value={isZh ? (VERBOSITY_ZH[activeConfig.expressionStyle.verbosity] || activeConfig.expressionStyle.verbosity) : activeConfig.expressionStyle.verbosity}
              onChange={v => {
                const reverseVerb: Record<string, string> = isZh
                  ? { '简洁':'concise','均衡':'balanced','详细':'detailed' }
                  : { concise:'concise', balanced:'balanced', detailed:'detailed' };
                updateDraft(['expressionStyle','verbosity'], reverseVerb[v] || v);
              }} />
          </div>
          <ReadonlyField label={t?.languagesField || ui('语言', 'Languages')} value={activeConfig.expressionStyle.languages.join(', ')} />
          {activeConfig.expressionStyle.vocabularyHints && activeConfig.expressionStyle.vocabularyHints.length > 0 && (
            <ReadonlyField label={t?.vocabularyHints || ui('词汇提示', 'Vocabulary Hints')} value={activeConfig.expressionStyle.vocabularyHints.join(', ')} />
          )}
          <ReadonlyField label={t?.ttsVoice || ui('TTS 声音', 'TTS Voice')} value={trVoice(activeConfig.ttsVoiceId) || t?.defaultVoice || ui('默认', 'default')} />
        </Section>

        {/* Boundaries */}
        <Section title={t?.behavioralBoundariesSection || ui('行为边界', 'Behavioral Boundaries')} section="boundaries" expanded={expandedSections} onToggle={toggleSection}>
          {(activeConfig.behavioralBoundariesZh && activeConfig.behavioralBoundariesZh.length > 0
            ? activeConfig.behavioralBoundariesZh
            : config.behavioralBoundaries
          ).map((b, i) => (
            <div key={i} className="flex items-center gap-2 p-3 bg-white/5 rounded-xl">
              <Activity size={12} className="text-indigo-400/50 shrink-0" />
              <span className="text-sm text-white/60">{b}</span>
            </div>
          ))}
          {(activeConfig.behavioralBoundariesZh || config.behavioralBoundaries).length === 0 && (
            <p className="text-white/45 text-xs">{t?.noBoundariesDefined || ui('尚未定义边界。', 'No boundaries defined.')}</p>
          )}
        </Section>

        {/* Tool Policy */}
        <Section title={t?.toolPolicySection || ui('工具策略', 'Tool Policy')} section="tools" expanded={expandedSections} onToggle={toggleSection}>
          <ReadonlyField label={t?.allowedToolsField || ui('允许工具', 'Allowed Tools')} value={(config.toolPolicy.allowedTools || ['*']).join(', ')} />
          <ReadonlyField label={t?.requireConfirmationField || ui('需要确认', 'Require Confirmation')} value={(config.toolPolicy.requireConfirmation || []).join(', ') || ui('无', 'none')} />
          <ReadonlyField label={t?.forbiddenToolsField || ui('禁用工具', 'Forbidden Tools')} value={(config.toolPolicy.forbiddenTools || []).join(', ') || ui('无', 'none')} />
          <ReadonlyField label={t?.maxIterationsField || ui('最大迭代次数', 'Max Iterations')} value={String(config.toolPolicy.maxIterations)} />
        </Section>

        {/* Memory Policy */}
        <Section title={t?.memoryPolicySection || ui('记忆策略', 'Memory Policy')} section="memory" expanded={expandedSections} onToggle={toggleSection}>
          <div className="grid grid-cols-2 gap-4">
            <ReadonlyField editMode={editMode} type="number" label={t?.retrieveLimitField || ui('检索上限', 'Retrieve Limit')}
              value={String(activeConfig.memoryPolicy.retrieveLimit)} onChange={v => updateDraft(['memoryPolicy','retrieveLimit'], Number(v))} />
            <ReadonlyField editMode={editMode} type="number" label={t?.minConfidenceField || ui('最低置信度', 'Min Confidence')}
              value={String(activeConfig.memoryPolicy.minConfidence)} onChange={v => updateDraft(['memoryPolicy','minConfidence'], Number(v))} />
          </div>
          <ReadonlyField label={t?.includeTypesField || ui('包含类型', 'Include Types')} value={activeConfig.memoryPolicy.includeTypes.join(', ')} />
          <ReadonlyField label={t?.autoExtractLabel || ui('自动提取', 'Auto-extract')} value={activeConfig.memoryPolicy.autoExtract ? ui('是', 'Yes') : ui('否', 'No')} />
        </Section>

      </div>

      {/* Evolution History + Radar */}
      <div className="rounded-2xl overflow-hidden">
        <PersonalityEvolution personalityId={config.id} />
      </div>
        </>
      )}
    </div>
  );
}

// Sub-components

function Section({ title, section, expanded, onToggle, children }: {
  title: string;
  section: string;
  expanded: Record<string, boolean>;
  onToggle: (s: string) => void;
  children: React.ReactNode;
}) {
  const open = expanded[section] !== false;
  return (
    <div className="xf-panel space-y-3 p-4">
      <button onClick={() => onToggle(section)} className="flex w-full items-center gap-2 text-left text-[13px] font-semibold text-white/70 hover:text-white/90" aria-expanded={open}>
        {open ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
        {title}
      </button>
      {open && <div className="space-y-3">{children}</div>}
    </div>
  );
}

function ReadonlyField({ label, value, mono, editMode, onChange, type, options }: {
  label: string;
  value: string;
  mono?: boolean;
  editMode?: boolean;
  onChange?: (val: string) => void;
  type?: 'text' | 'number' | 'select';
  options?: string[];
}) {
  return (
    <div className="space-y-1">
      <label className="text-[12px] font-semibold text-white/45">{label}</label>
      {editMode && onChange ? (
        type === 'select' && options ? (
          <select value={value} onChange={e => onChange(e.target.value)}
            className="xf-panel w-full rounded-xl px-3 py-2 text-sm text-white/80 bg-white/[0.04] border border-indigo-500/20 focus:border-indigo-400/40 outline-none">
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : type === 'number' ? (
          <input type="number" value={value} onChange={e => onChange(e.target.value)} step="0.1"
            className="xf-panel w-full rounded-xl px-3 py-2 text-sm text-white/80 bg-white/[0.04] border border-indigo-500/20 focus:border-indigo-400/40 outline-none font-mono" />
        ) : (
          <input type="text" value={value} onChange={e => onChange(e.target.value)}
            className={`xf-panel w-full rounded-xl px-3 py-2 text-sm text-white/80 bg-white/[0.04] border border-indigo-500/20 focus:border-indigo-400/40 outline-none ${mono ? 'font-mono' : ''}`} />
        )
      ) : (
        <div className={`xf-panel w-full rounded-xl px-3 py-2 text-sm text-white/65 ${mono ? 'font-mono' : ''}`}>
          {value || <span className="text-white/45">—</span>}
        </div>
      )}
    </div>
  );
}
